/* eslint-disable @typescript-eslint/no-non-null-assertion */
import * as R from "remeda";
import type {
  LoopRecordListActionGroupConfig,
  ActionConfig,
  ActionReference,
  TriggerConfig,
  TriggerReference,
} from "@smartsuite/automation-engine-api";

import type {
  ActionCandidate,
  ActionGroupType,
  InputCandidate,
  TriggerCandidate,
} from "../../../../../../../../types/candidate";
import { getOutputDescriptions } from "../../../../../../../../utils/conditions-coverter.hook";
import {
  isSupportedOutput,
  memoizedConvertOutputToDynamicSectionOption,
} from "../../../../utils";
import type { FieldTypePillProps } from "@smartsuite/react-ui/lib";
import { usePillTooltip } from "../../../../../../../../utils/make-tooltip.hook";
import { findMatchedAction } from "../../../../../../../../utils/match-action";
import { t } from "i18next";
import { rootStore } from "../../../../../../../../stores";
import type { LoopingOptionGroup } from "../../../../MemberEditor/utils";
import { CURRENT_ITEM_OUTPUT_ID } from "../../../../../../../../utils/constants";
import type {
  DynamicSection,
  DynamicSectionOption,
} from "../../../../../../../../types/line";

export interface Option {
  title: string;
  icon: string;
  outputId: string;
}

export interface NumberPill extends FieldTypePillProps {
  type: "triggerOutput" | "actionOutput" | "custom" | "loop";
  value: string;
  title: string;
  icon: string;
}

export const triggerToOptions = (trigger: TriggerCandidate): Option[] =>
  getOutputDescriptions(trigger).map((output) => ({
    title: output.label,
    outputId: output.outputId,
    icon: output.icon,
  }));

interface ConvertersFunctions {
  convertInputToPills: (
    input: InputCandidate,
    triggerOptions: Option[],
    actionOptions: Option[],
    trigger: TriggerConfig,
    actions: ActionConfig[],
    currentAction?: ActionCandidate,
    loopOptions?: LoopingOptionGroup,
  ) => NumberPill[];
}

export const useConverters = (): ConvertersFunctions => {
  const {
    makeTriggerPillTooltip,
    makeActionPillTooltip,
    makeCurrentItemPillTooltip,
  } = usePillTooltip();

  const convertInputToPills = (
    input: InputCandidate,
    triggerOptions: Option[],
    actionOptions: Option[],
    trigger: TriggerConfig,
    actions: ActionConfig[],
    currentAction?: ActionCandidate,
    loopOptions?: LoopingOptionGroup,
  ): NumberPill[] => {
    const rootLooping = rootStore.automations.candidate.looping;
    const looping = currentAction
      ? (rootLooping?.scopeForAction?.(currentAction.hash) ?? rootLooping)
      : rootLooping;

    return (
      input.value?.values.flatMap((value): NumberPill[] => {
        switch (value.value.oneofKind) {
          case "reference":
            if (value.value.reference.outputId === CURRENT_ITEM_OUTPUT_ID) {
              return [
                {
                  type: "loop",
                  value: JSON.stringify(value.value.reference),
                  icon: "",
                  title: t("general.current-item"),
                  tooltip: makeCurrentItemPillTooltip(looping),
                },
              ];
            }
            if (value.value.reference.source.oneofKind === "triggerReference") {
              const { outputId } = value.value.reference;

              let triggerOption = triggerOptions.find(
                (option) => option.outputId === outputId,
              );

              if (triggerOption === undefined && loopOptions?.options.length) {
                triggerOption = loopOptions.options.find(
                  (option) => option.outputId === outputId,
                ) as Option;
              }

              return [
                {
                  type: "triggerOutput",
                  value: outputId,
                  icon: triggerOption?.icon ?? "",
                  title:
                    triggerOption?.title ??
                    String(t("automation-form.deleted-field-title")),
                  tooltip: makeTriggerPillTooltip(trigger),
                  isError: !triggerOption?.title,
                },
              ];
            } else if (
              value.value.reference.source.oneofKind === "actionReference"
            ) {
              const { outputId } = value.value.reference;
              const actionOption = actionOptions.find(
                (option) => option.outputId === outputId,
              );

              const pillAction = findMatchedAction(
                value.value.reference.source,
                actions,
              );

              const isSupported =
                pillAction === undefined
                  ? false
                  : isSupportedOutput(
                      pillAction,
                      actions,
                      currentAction,
                      looping,
                    );

              if (
                value.value.reference.outputId.startsWith("currentItem|") ||
                value.value.reference.outputId.startsWith("currentItem.")
              ) {
                const outputOptions =
                  memoizedConvertOutputToDynamicSectionOption(
                    looping,
                    rootStore.automations.candidate.allActions,
                    rootStore.automations.candidate.self.trigger,
                  );

                const outputOption = outputOptions?.options.find(
                  (option) => option.outputId === outputId,
                );

                return [
                  {
                    type: "actionOutput",
                    title:
                      outputOption?.label ??
                      String(t("automation-form.deleted-field-title")),
                    icon: outputOption?.icon ?? "",
                    isError: !isSupported,
                    value: outputId,
                    tooltip:
                      pillAction === undefined
                        ? undefined
                        : makeActionPillTooltip(pillAction),
                  } as const,
                ];
              }

              return [
                {
                  type: "actionOutput",
                  value: outputId,
                  icon: actionOption?.icon ?? "",
                  title:
                    actionOption?.title ??
                    String(t("automation-form.deleted-field-title")),
                  isError: !isSupported || !actionOption?.title,
                  tooltip:
                    pillAction === undefined
                      ? undefined
                      : makeActionPillTooltip(pillAction),
                },
              ];
            } else {
              return [];
            }
          case "function":
            return [];
          case "string": {
            const { string } = value.value;
            return [{ type: "custom", value: string, title: string, icon: "" }];
          }
          case "number": {
            const { number } = value.value;
            return [
              {
                type: "custom",
                value: number.toString(),
                title: number.toString(),
                icon: "",
              },
            ];
          }
          default:
            return [];
        }
      }) ?? []
    );
  };

  return {
    convertInputToPills,
  };
};

export const mergeNextPillToInput = (
  input: InputCandidate,
  pill:
    | {
        type: "triggerOutput";
        triggerReference: TriggerReference;
        outputId: string;
      }
    | {
        type: "actionOutput";
        actionReference: ActionReference;
        outputId: string;
      }
    | {
        type: "custom";
        number: string;
      }
    | { type: "loop"; source: LoopRecordListActionGroupConfig["source"] },
): InputCandidate => {
  return {
    ...input,
    value: {
      values: [
        ...(R.isTruthy(input.value) ? input.value.values : []),
        ...(() => {
          switch (pill.type) {
            case "triggerOutput":
              return [
                {
                  value: {
                    oneofKind: "reference",
                    reference: {
                      source: {
                        oneofKind: "triggerReference",
                        triggerReference: pill.triggerReference,
                      },
                      outputId: pill.outputId,
                    },
                  } as const,
                },
              ];
            case "actionOutput":
              return [
                {
                  value: {
                    oneofKind: "reference",
                    reference: {
                      source: {
                        oneofKind: "actionReference",
                        actionReference: pill.actionReference,
                      },
                      outputId: pill.outputId,
                    },
                  } as const,
                },
              ];
            case "custom": {
              const value = pill.number.replace(",", ".");
              return [
                {
                  value: {
                    oneofKind: "number",
                    number: +value,
                  } as const,
                },
              ];
            }
            case "loop":
              if (pill.source.oneofKind === "outputReference") {
                return [
                  {
                    value: {
                      oneofKind: "reference",
                      reference: pill.source.outputReference,
                    } as const,
                  },
                ];
              }

              return [];
            default:
              return [];
          }
        })(),
      ],
    },
  };
};

export const ejectPillFromInput = (
  input: InputCandidate,
  index: number,
): InputCandidate => ({
  ...input,
  value: { values: input.value?.values.filter((_, i) => i !== index) ?? [] },
});

export const getIsMenuAvaliable = (
  triggerOutputs: DynamicSectionOption[],
  actionMenus: DynamicSection[],
  actionGroupType: ActionGroupType,
  isConfiguredLoopingSourceValid: boolean,
): boolean => {
  return (
    triggerOutputs.length > 0 ||
    actionMenus.some((menu) => menu.options.length > 0) ||
    (actionGroupType === "loop" && isConfiguredLoopingSourceValid)
  );
};
